import { useCallback, type ReactNode } from 'react';
import {
  makeStyles,
  tokens,
  Label,
  Text,
} from '@fluentui/react-components';
import { TokenizedInput } from './TokenizedInput';
import { ExpressionPreview } from './ExpressionPreview';
import { EndActionsSlot } from '../shared/EndActionsSlot';
import { useFieldPreviewFocus } from './useFieldPreviewFocus';
import type { AgentParameterSchema } from './AgentParameterMenu';
import type { ValueSegment } from '@/types/valueSegment';
import { generateSegmentId, ValueSegmentType, isValueSegmentArray, segmentsToString } from '@/types/valueSegment';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  label: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },
  required: {
    color: tokens.colorPaletteRedForeground1,
    marginLeft: tokens.spacingHorizontalXXS,
  },
  error: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
  // Wrapper for input + preview overlay positioning
  inputWrapper: {
    position: 'relative',
  },
});

export interface StringFieldProps {
  name: string;
  label: string;
  value: string | ValueSegment[];
  onChange: (value: string | ValueSegment[]) => void;
  description?: string | undefined;
  placeholder?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  error?: string | undefined;
  /** Show the agent parameter button - for tools within an agent */
  showAgentParameterButton?: boolean | undefined;
  /** The agent parameter schema from the tool definition */
  agentParameterSchema?: AgentParameterSchema | undefined;
  /** Action outputs from the last successful run, keyed by action name */
  actionOutputs?: Record<string, unknown> | undefined;
  /** Whether we have run data available for previews */
  hasRunData?: boolean | undefined;
  /** Whether the workflow has changed since the last run (data may be stale) */
  isStale?: boolean | undefined;
  /**
   * Optional slot rendered inside the input box on the right edge,
   * vertically centered. Used to host inline affordances like
   * `FieldActionChips` so they live inside the bordered input rather
   * than floating above it.
   */
  endActions?: ReactNode | undefined;
}

/**
 * StringField - Single-line text input with token support
 * Displays tokens as visual badges that can be clicked to view/edit
 */
export const StringField = ({
  name,
  label,
  value,
  onChange,
  description,
  placeholder,
  required,
  disabled,
  error,
  showAgentParameterButton,
  agentParameterSchema,
  actionOutputs,
  hasRunData,
  isStale,
  endActions,
}: StringFieldProps) => {
  const styles = useStyles();
  const { containerHandlers, previewHandlers } = useFieldPreviewFocus();

  // Convert string to segments if needed
  const normalizedValue: ValueSegment[] = isValueSegmentArray(value)
    ? value
    : typeof value === 'string' && value
      ? [{
          id: generateSegmentId(),
          type: ValueSegmentType.LITERAL,
          value: value,
        }]
      : [];

  // Get the string value for preview
  const stringValue = segmentsToString(normalizedValue);

  // Handle change from TokenizedInput
  const handleChange = useCallback((segments: ValueSegment[]) => {
    onChange(segments);
  }, [onChange]);

  return (
    <div className={styles.container} {...containerHandlers}>
      <div className={styles.labelRow}>
        <Label className={styles.label} htmlFor={name}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </Label>
        {endActions && <EndActionsSlot>{endActions}</EndActionsSlot>}
      </div>

      {/* Wrapper provides relative positioning for the overlay preview */}
      <div className={styles.inputWrapper}>
        <TokenizedInput
          name={name}
          value={normalizedValue}
          onChange={handleChange}
          placeholder={placeholder || description}
          multiline={false}
          disabled={disabled}
          readonly={false}
          showAgentParameterButton={showAgentParameterButton}
          agentParameterSchema={agentParameterSchema}
          actionOutputs={actionOutputs}
        />

        {/* Preview button — user-triggered evaluation in isolated worker */}
        {stringValue && (
          <ExpressionPreview
            value={stringValue}
            actionOutputs={actionOutputs}
            hasRunData={hasRunData}
            isStale={isStale}
            mode="overlay"
            {...previewHandlers}
          />
        )}
      </div>

      {error && (
        <Text className={styles.error} role="alert">
          {error}
        </Text>
      )}
    </div>
  );
};

StringField.displayName = 'StringField';
